/**
 * Shopping Cart Context
 * Global state management for e-commerce cart
 */

import { createContext, useContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { CartItem, Product, SizeOption } from '../types/commerce';
import { calculateBulkDiscount } from '../data/products';

interface CartState {
  items: CartItem[];
  subtotal: number;
  totalDiscount: number;
  total: number;
}

type CartAction =
  | { type: 'ADD_ITEM'; product: Product; size: SizeOption; quantity: number }
  | { type: 'REMOVE_ITEM'; productId: string; sizeId: string }
  | { type: 'UPDATE_QUANTITY'; productId: string; sizeId: string; quantity: number }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; items: CartItem[] };

interface CartContextType extends CartState {
  addItem: (product: Product, size: SizeOption, quantity: number) => void;
  removeItem: (productId: string, sizeId: string) => void;
  updateQuantity: (productId: string, sizeId: string, quantity: number) => void;
  clearCart: () => void;
  getItemCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = 'mfs_shopping_cart';

// Calculate cart item totals with bulk discounts
function calculateCartItem(product: Product, size: SizeOption, quantity: number): CartItem {
  const itemPrice = product.basePrice + size.priceModifier;
  const subtotal = itemPrice * quantity;
  const discountPercent = calculateBulkDiscount(product, quantity);
  const discount = (subtotal * discountPercent) / 100;
  const total = subtotal - discount;

  return {
    product,
    selectedSize: size,
    quantity,
    subtotal,
    discount,
    total
  };
}

// Calculate cart totals
function calculateTotals(items: CartItem[]): Pick<CartState, 'subtotal' | 'totalDiscount' | 'total'> {
  const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
  const totalDiscount = items.reduce((sum, item) => sum + item.discount, 0);
  const total = items.reduce((sum, item) => sum + item.total, 0);

  return { subtotal, totalDiscount, total };
}

function cartReducer(state: CartState, action: CartAction): CartState {
  let newItems: CartItem[];

  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, size, quantity } = action;
      const existingItemIndex = state.items.findIndex(
        item => item.product.id === product.id && item.selectedSize.id === size.id
      );

      if (existingItemIndex >= 0) {
        // Update existing item quantity
        newItems = state.items.map((item, index) => {
          if (index === existingItemIndex) {
            const newQuantity = item.quantity + quantity;
            return calculateCartItem(product, size, newQuantity);
          }
          return item;
        });
      } else {
        // Add new item
        const newItem = calculateCartItem(product, size, quantity);
        newItems = [...state.items, newItem];
      }

      const totals = calculateTotals(newItems);
      return { items: newItems, ...totals };
    }

    case 'REMOVE_ITEM': {
      newItems = state.items.filter(
        item => !(item.product.id === action.productId && item.selectedSize.id === action.sizeId)
      );
      const totals = calculateTotals(newItems);
      return { items: newItems, ...totals };
    }

    case 'UPDATE_QUANTITY': {
      if (action.quantity <= 0) {
        return cartReducer(state, {
          type: 'REMOVE_ITEM',
          productId: action.productId,
          sizeId: action.sizeId
        });
      }

      newItems = state.items.map(item => {
        if (item.product.id === action.productId && item.selectedSize.id === action.sizeId) {
          return calculateCartItem(item.product, item.selectedSize, action.quantity);
        }
        return item;
      });

      const totals = calculateTotals(newItems);
      return { items: newItems, ...totals };
    }

    case 'CLEAR_CART':
      return {
        items: [],
        subtotal: 0,
        totalDiscount: 0,
        total: 0
      };

    case 'LOAD_CART': {
      const totals = calculateTotals(action.items);
      return { items: action.items, ...totals };
    }

    default:
      return state;
  }
}

const initialState: CartState = {
  items: [],
  subtotal: 0,
  totalDiscount: 0,
  total: 0
};

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const items = JSON.parse(savedCart) as CartItem[];
        dispatch({ type: 'LOAD_CART', items });
      }
    } catch (error) {
      console.error('Failed to load cart from storage:', error);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(state.items));
    } catch (error) {
      console.error('Failed to save cart to storage:', error);
    }
  }, [state.items]);

  const addItem = (product: Product, size: SizeOption, quantity: number) => {
    dispatch({ type: 'ADD_ITEM', product, size, quantity });
  };

  const removeItem = (productId: string, sizeId: string) => {
    dispatch({ type: 'REMOVE_ITEM', productId, sizeId });
  };

  const updateQuantity = (productId: string, sizeId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', productId, sizeId, quantity });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getItemCount = () => {
    return state.items.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        getItemCount
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
